import React, {
  FC, ReactElement, useEffect, useRef, useState,
} from 'react';
import DynamicForm, { FormEntry, FormValues } from '@ynomia/dynamic-form';
import { Modal, Typography } from 'antd';
import { Asset } from '@ynomia/core/dist/project/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import Icon from '@mdi/react';
import { mdiAlert, mdiCheckDecagram } from '@mdi/js';
import styles from './styles.module.less';
import { analytics, client, updateAssetDestination } from '../../../../../services';
import ModalForm from '../../../../atoms/ModalForm';
import { getContextStores } from '../../../../../context/helper';
import { SYSTEM_TYPE } from '../../../../../config/types';
import LayerSegmentedPickerHelper from '../../../../../utils/layerSegmentedPickerHelper';
import { layerSegmentedPickerHelper } from '../../../../../utils';
import { getAssets } from '../../../../../actions/assets';
import {
  getAssetsKeyedByTwinId,
  getLayerChildrenMap,
  getLayerSegmentedPickerColumns,
  getScratchProjectCode,
  getTenant,
  getSelectedAssetType,
} from '../../../../../selectors';

interface Props {
  assetId: string,
}

enum Stage {
  INITIAL,
  LOCATION_VERIFIED,
  PENDING_VERIFICATION,
  FIELD_CONSTRAINT_ERROR,
  NOT_RECOGNISED_RESTRICTED,
  NOT_RECOGNISED,
  NOT_RECOGNISED_EDIT,
}

const EditDestination: FC<Props> = ({
  assetId,
}) => {
  const contextStores = getContextStores();
  const { layerState, assetState, assetDispatch } = contextStores;

  const { assetsKeyedById, lastFetchStartTime } = assetState;
  const { layersKeyedById } = layerState;
  const layerChildrenMap = getLayerChildrenMap(contextStores);
  const tenant = getTenant(contextStores);
  const projectCode = getScratchProjectCode(contextStores);
  const assetsKeyedByTwinId = getAssetsKeyedByTwinId(contextStores);
  const layerSegmentedPickerColumns = getLayerSegmentedPickerColumns(contextStores);
  const selectedAssetType = getSelectedAssetType(contextStores);

  const currentAsset = assetsKeyedById.get(assetId)!;
  const shouldPrefill = currentAsset?.eventMetrics?.[SYSTEM_TYPE.DESTINATION_UPDATE]?.count
  || selectedAssetType?.destination_update_prefill_enabled;
  const [stage, setStage] = useState<Stage>(Stage.INITIAL);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [fieldConstraintValues, setFieldConstraintValues] = useState<{
    assetConstraintValue: string;
    swapConstraintValue: string;
  } | undefined>(undefined);
  const dynamicFormRef = useRef<DynamicForm | null>(null);
  const textFieldPrefix = selectedAssetType.destination_update_default_prefix;

  const [defaultValues, setDefaultValues] = useState<{ [key: string]: any }>({
    twinID: textFieldPrefix,
  });

  const showModal = () => {
    setIsModalOpen(true);
    analytics.trackEvent('Edit Destination Opened', { asset_type: assetId });
  };

  const dismissModal = () => {
    setStage(Stage.INITIAL);
    setIsModalOpen(false);
    setIsFormReady(false);
  };

  const getAssetWithNewTwinID = (newTwinID?: string): Asset | undefined => (
    (newTwinID ? assetsKeyedByTwinId.get(newTwinID) : undefined) as Asset | undefined
  );

  const fieldConstraintCheckSuccessful = (assetToSwapWith: Asset | undefined): boolean => {
    const fieldConstraint = selectedAssetType.destination_update_field_constraint;
    const assetConstraintValue = fieldConstraint && currentAsset?.fields?.[fieldConstraint];
    const swapConstraintValue = fieldConstraint && assetToSwapWith?.fields?.[fieldConstraint];
    setFieldConstraintValues({
      assetConstraintValue,
      swapConstraintValue,
    });
    if ((!assetConstraintValue && !swapConstraintValue) || !swapConstraintValue) return true;
    return assetConstraintValue === swapConstraintValue;
  };

  const canSaveForm = (): boolean => (
    [Stage.LOCATION_VERIFIED, Stage.NOT_RECOGNISED].includes(stage)
  );

  const handleVerifyTwinID = (twinID: string): void => {
    const assetToSwapWith = getAssetWithNewTwinID(twinID);

    if (!fieldConstraintCheckSuccessful(assetToSwapWith)) {
      setStage(Stage.FIELD_CONSTRAINT_ERROR);
      return;
    }

    const layer = layersKeyedById.get(assetToSwapWith?.destination?.layerID || '');
    const newSegmentedDestination = layer?.id
      ? layerSegmentedPickerHelper.segmentedDestinationDefaultValue(layersKeyedById, layer.id)
      : defaultValues.segmentedDestination;

    setDefaultValues({
      twinID,
      segmentedDestination: newSegmentedDestination,
      confirmNotRecognised: undefined,
    });

    dynamicFormRef.current?.resetForm({
      values: {
        twinID,
        segmentedDestination: layer?.id
          && layerSegmentedPickerHelper.segmentedDestinationDefaultValue(layersKeyedById, layer.id),
        confirmNotRecognised: undefined,
      },
    });

    if (getAssetWithNewTwinID(twinID)) {
      setStage(Stage.LOCATION_VERIFIED);
    } else {
      const allowUnknownLocation = client.session
        .checkFeature('assets.flags.destination_update_allow_unknown_location', false);
      setStage(allowUnknownLocation ? Stage.NOT_RECOGNISED : Stage.NOT_RECOGNISED_RESTRICTED);
    }
  };

  const saveData = async (formValues: FormValues) => {
    const { segmentedDestination, twinID } = formValues;
    const destinationLayerIds = (segmentedDestination as { [layerType: string]: string });
    const destinationLayerId = LayerSegmentedPickerHelper.getYoungestLayer(
      layerChildrenMap,
      destinationLayerIds,
    );
    setSubmitLoading(true);
    await updateAssetDestination(
      tenant,
      projectCode,
      currentAsset?.id,
      twinID,
      destinationLayerId!,
    );

    await getAssets(
      tenant,
      projectCode,
      [selectedAssetType],
      lastFetchStartTime,
      assetDispatch,
    );

    setSubmitLoading(false);
    setIsModalOpen(false);
  };

  const handleOnSubmit = (formValues: FormValues): void => {
    switch (stage) {
      case Stage.INITIAL:
      case Stage.PENDING_VERIFICATION:
      case Stage.NOT_RECOGNISED_RESTRICTED:
      case Stage.NOT_RECOGNISED_EDIT:
        handleVerifyTwinID(formValues.twinID);
        break;
      case Stage.LOCATION_VERIFIED:
      case Stage.NOT_RECOGNISED:
        saveData(formValues);
        break;
      default:
        break;
    }
  };

  const renderInstructions = () => {
    let icon: ReactElement;
    let title: string;
    let description: string;
    let color: string;
    switch (stage) {
      case Stage.LOCATION_VERIFIED:
        color = '#00de98';
        icon = (
          <Icon
            path={mdiCheckDecagram}
            size={2}
            color={color}
          />
        );
        title = 'Location Verified';
        description = 'Please confirm the below details are\ncorrect before saving.';
        break;
      case Stage.FIELD_CONSTRAINT_ERROR:
        color = '#fb434a';
        icon = (
          <Icon
            path={mdiAlert}
            size={2}
            color={color}
          />
        );
        title = 'Location Not Allowed';
        // eslint-disable-next-line no-multi-str, max-len
        description = `The asset you are installing is a "${fieldConstraintValues?.assetConstraintValue}", \
but the location code you entered is for a "${fieldConstraintValues?.swapConstraintValue}". \
Please double check the drawing and try again.`;
        break;
      case Stage.NOT_RECOGNISED_RESTRICTED:
      case Stage.NOT_RECOGNISED:
      case Stage.NOT_RECOGNISED_EDIT:
        color = '#e8a924';
        icon = (
          <Icon
            path={mdiAlert}
            size={2}
            color={color}
          />
        );
        title = 'Location Not Recognised';
        // eslint-disable-next-line no-multi-str
        description = 'Ynomia was unable to verify the Location\nCode you entered. \
Please double check\nand/or edit the details below.';
        break;
      case Stage.PENDING_VERIFICATION:
        color = '#666666';
        icon = (
          <FontAwesomeIcon
            icon={faQuestionCircle}
            color={color}
            fontSize={50}
          />
        );
        title = 'Pending Verification';
        description = 'Let us validate the updated Location\nCode by clicking "Next" below.';
        break;
      default:
        return null;
    }

    return (
      <div className={styles.instructionContainer} style={{ color }}>
        <div className={styles.iconContainer} style={{ color }}>{icon}</div>
        <span className={styles.instructionTitle} style={{ color }}>{title}</span>
        <span className={styles.instructionDescription} style={{ color }}>{description}</span>
      </div>
    );
  };

  const handleDynamicFormRef = (ref: DynamicForm | null) => {
    if (!ref) {
      setIsFormReady(false);
      return;
    }

    dynamicFormRef.current = ref;
    setIsFormReady(true);
  };

  useEffect(() => {
    const defaultTwinID = shouldPrefill && currentAsset?.destination?.twinID?.[0];
    if (defaultTwinID && (isModalOpen && isFormReady)) {
      handleVerifyTwinID(defaultTwinID);
    }
  }, [isModalOpen, isFormReady]);

  const renderForm = () => {
    const fields: Array<FormEntry> = [
      {
        entryComponent: 'text',
        id: 'twinID',
        label: 'Location Code',
        isRequired: true,
        properties: {
          removeDoubleQuotes: true,
          placeholder: 'Scan code or enter text...',
          autoFocus: [Stage.NOT_RECOGNISED_EDIT, Stage.PENDING_VERIFICATION].includes(stage),
          uppercase: true,
          ...([Stage.NOT_RECOGNISED_RESTRICTED, Stage.PENDING_VERIFICATION].includes(stage)
            ? {}
            : { variant: 'filled', style: { cursor: 'pointer' } }
          ),
        },
      },
      {
        entryComponent: 'segmented_picklist',
        id: 'segmentedDestination',
        label: 'Destination',
        properties: {
          columns: layerSegmentedPickerColumns,
          resetValuesOnChange: true,
        },
        isRequired: true,
        disabled: stage === Stage.LOCATION_VERIFIED,
        show: `stage in (${Stage.LOCATION_VERIFIED}, ${Stage.NOT_RECOGNISED})`,
      },
      {
        entryComponent: 'checkbox',
        id: 'confirmNotRecognised',
        label: 'I have confirmed that the above details are correct.',
        properties: {
        },
        isRequired: true,
        show: `stage == ${Stage.NOT_RECOGNISED}`,
      },
    ];

    const onFormEvent = (key: string, event: string) => {
      if (key === 'twinID' && event === 'onFocus') {
        switch (stage) {
          case Stage.LOCATION_VERIFIED:
          case Stage.FIELD_CONSTRAINT_ERROR:
            setStage(Stage.PENDING_VERIFICATION);
            break;
          case Stage.NOT_RECOGNISED:
          case Stage.NOT_RECOGNISED_RESTRICTED:
            setStage(Stage.NOT_RECOGNISED_EDIT);
            break;
          default:
            break;
        }
      }
    };
    return (
      <ModalForm
        dynamicFormRef={ref => handleDynamicFormRef(ref)}
        fields={fields}
        onCancel={dismissModal}
        disableSubmitButton={
          [
            Stage.NOT_RECOGNISED_RESTRICTED,
            Stage.FIELD_CONSTRAINT_ERROR,
          ].includes(stage)
        }
        filtrexProps={{ stage }}
        defaultValues={defaultValues}
        onSubmit={handleOnSubmit}
        onEvent={onFormEvent}
        submitButtonText={canSaveForm() ? 'Save' : 'Next'}
        submitButtonLoading={submitLoading || false}
      />
    );
  };

  return (
    <>
      <Typography.Link onClick={showModal}>
        Update Location
      </Typography.Link>
      <Modal
        title="Update Asset Location"
        open={isModalOpen}
        closable={false}
        footer={[]}
        style={{ padding: 0 }}
        destroyOnClose
      >
        {renderInstructions()}
        {renderForm()}
      </Modal>
    </>
  );
};

export default EditDestination;
